/**
 * @fileoverview React wrapper for the full event feed message layout.
 */

import React from 'react';

import MessageAvatar, { MessageAvatarProps } from './MessageAvatar';
import MessageDate, { MessageDateProps } from './MessageDate';
import MessageDisplay, { MessageDisplayProps } from './MessageDisplay';
import MessageEdit, { MessageEditProps } from './MessageEdit';
import MessageFailed, { MessageFailedProps } from './MessageFailed';
import MessageSmsIndicator from './MessageSmsIndicator';
import MessageTime, { MessageTimeProps } from './MessageTime';
import MessageToolbar, { MessageToolbarProps } from './MessageToolbar';

export interface MessageProps
  extends MessageDateProps,
    MessageAvatarProps,
    Omit<MessageToolbarProps, 'onDelete'>,
    MessageDisplayProps,
    MessageEditProps,
    Omit<MessageFailedProps, 'onDelete'>,
    MessageTimeProps {
  displayPayloads?: Array<MessageDisplayProps['payload']>;
  onDelete?: MessageToolbarProps['onDelete'];
  onFailedDelete?: MessageFailedProps['onDelete'];
  onToolbarDelete?: MessageToolbarProps['onDelete'];
  showAvatar?: boolean;
  showDate?: boolean;
  showEdit?: boolean;
  showFailed?: boolean;
  showSmsIndicator?: boolean;
  showTime?: boolean;
  showToolbar?: boolean;
}

const Message: React.FC<MessageProps> = ({
  showDate = false,
  date,
  showAvatar = false,
  isFirst,
  avatarEmail,
  displayName,
  initials,
  isAuthorClient,
  showSmsIndicator = false,
  showToolbar = false,
  onToolbarDelete,
  onEdit,
  onCopyError,
  onCopySuccess,
  rawMessagePayload,
  showEdit = false,
  onCancel,
  onChange,
  onSave,
  onDelete,
  value,
  clientId,
  isFormattingEnabled,
  isBookkeeper,
  isInProgress,
  payload,
  displayPayloads,
  sentWithSms,
  showFailed = false,
  onFailedDelete,
  onResend,
  showTime = false,
  hasReadReceipt,
  showMobileIcon,
}) => {
  const resolvedDisplayPayloads = displayPayloads || (payload !== undefined ? [payload] : []);

  return (
    <>
      {showDate ? <MessageDate date={date} /> : null}

      <div className="bl-message_message-container">
        {showAvatar ? (
          <MessageAvatar
            avatarEmail={avatarEmail}
            displayName={displayName}
            initials={initials}
            isAuthorClient={isAuthorClient}
            isFirst={isFirst}
          />
        ) : null}
        {showSmsIndicator ? <MessageSmsIndicator /> : null}
        <div
          className={['bl-message_message-container_assets', 'bl-card', showEdit ? 'editable' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {showToolbar ? (
            <MessageToolbar
              onCopyError={onCopyError}
              onCopySuccess={onCopySuccess}
              onDelete={onToolbarDelete ?? onDelete}
              onEdit={onEdit}
              rawMessagePayload={rawMessagePayload}
            />
          ) : null}
          {!showEdit
            ? resolvedDisplayPayloads.map((displayPayload, index) => (
                <MessageDisplay
                  clientId={clientId}
                  isBookkeeper={isBookkeeper}
                  isFormattingEnabled={isFormattingEnabled}
                  isInProgress={isInProgress}
                  key={`${index}-${displayPayload || ''}`}
                  payload={displayPayload}
                  sentWithSms={sentWithSms}
                />
              ))
            : null}
          {showEdit ? (
            <MessageEdit onCancel={onCancel} onChange={onChange} onSave={onSave} value={value} />
          ) : null}
        </div>
      </div>

      {showFailed ? (
        <MessageFailed onDelete={onFailedDelete ?? onDelete} onResend={onResend} />
      ) : null}

      {showTime ? (
        <MessageTime date={date} hasReadReceipt={hasReadReceipt} showMobileIcon={showMobileIcon} />
      ) : null}
    </>
  );
};

export default Message;
